import { InfoIcon } from '@carboncopyco/icons';
import {
  BarrierOneColorIllustration,
  CloneIllustration,
  LogoutIllustration,
} from '@carboncopyco/illustrations';
import {
  Button,
  Checkbox,
  ColorHex,
  Spacer,
  TextBase,
  TextLargeParagraphTitle,
  Tooltip,
} from '@carboncopyco/ui';
import { type RefObject, useEffect, useRef } from 'react';

export interface WarningBeforeRecordingProps {
  isVisible: boolean;
  anchorRef: RefObject<any>;
  onLearnMoreClick: () => void;
  onClose: () => void;
  onAcceptTermAndStartCloning: () => void;
  isChecked: boolean;
  setIsChecked: (isChecked: boolean) => void;
}

export const WarningBeforeRecording = ({
  isVisible,
  anchorRef,
  onLearnMoreClick,
  onClose,
  onAcceptTermAndStartCloning,
  isChecked,
  setIsChecked,
}: WarningBeforeRecordingProps) => {
  const acceptBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (isVisible) {
      acceptBtnRef.current?.focus();
    }
  }, [isVisible]);

  return (
    <Tooltip
      id="warningBeforeRecording"
      arrowPosition="end"
      visible={isVisible}
      placement="bottom-end"
      anchorRef={anchorRef}
    >
      <div className="flex flex-col p-5 shadow-ds-edit-tool rounded-md">
        <div className="flex items-center">
          <InfoIcon
            color={ColorHex.secondary400}
            size={20}
          />
          <Spacer amount={0.5} />
          <TextLargeParagraphTitle
            label="Plan to share this externally?"
            color="text-ds-natural-600"
          />
        </div>
        <Spacer amount={0.75} />
        <div className="flex items-center">
          <TextBase
            label="Here are some tips for sharing securely:"
            color="text-ds-natural-600"
          />
          <Spacer amount={0.25} />
        </div>
        <Spacer amount={1.25} />
        <div className="flex justify-between text-center">
          <div className="w-40 flex flex-col px-1.5 justify-center text-center items-center">
            <BarrierOneColorIllustration />
            <Spacer />
            <TextBase
              label="Avoid cloning sensitive data"
              color="text-ds-natural-600"
            />
          </div>
          <div className="w-40 flex flex-col px-1.5 justify-center text-center items-center">
            <CloneIllustration />
            <Spacer />
            <TextBase
              label="Use demo account credentials"
              color="text-ds-natural-600"
            />
          </div>

          <div className="w-40 flex flex-col px-1.5 justify-center text-center items-center">
            <LogoutIllustration />
            <Spacer />
            <TextBase
              label="Log out before completing the clone"
              color="text-ds-natural-600"
            />
          </div>
        </div>
        <Spacer amount={2} />
        <div>
          <Checkbox
            checked={isChecked}
            onChange={(element) => setIsChecked(element.target.checked)}
            name="hideWarning"
            label={
              <TextBase
                label={"Don't show this message again"}
                color="text-ds-natural-600"
              />
            }
          />
        </div>
        <Spacer amount={1} />
        <div className="flex justify-end gap-5">
          <Button
            onClick={onLearnMoreClick}
            label="Learn more"
            blue
            link
          />
          <Button
            id="acceptButton"
            ref={acceptBtnRef}
            onClick={() => {
              onAcceptTermAndStartCloning();
              onClose();
            }}
            label="Got it, start cloning"
          />
        </div>
      </div>
    </Tooltip>
  );
};
